/**
 * RYZE Sticky Add-to-Cart Bar - PDP, PHONES ONLY
 * ──────────────────────────────────────────────────────────
 * W7 2026-10-18 (owner order): the floating capsule retires. The bar is
 * SVNTY's construction (snippets/svnty-pdp-sticky.liquid + its script)
 * dressed in the RYZE system: a full-width bar docked to the bottom edge,
 * square to the screen, one hairline on top, opaque white paper, the 16px
 * gutter, safe-area padded. ONE price line (the product name + the locked
 * price voice: struck compare-at, sale figure, the red chip) over ONE 48px
 * control row: thumbnail, the SIZE menu (opens upward, SVNTY's listbox),
 * the boxed stepper, THE RYZE BUTTON. Styles: assets/ryze-sticky-atc.css.
 *
 * PHONES ONLY: built below 990px. At 990 and up nothing is built, nothing
 * is fetched, nothing observes (one MediaQueryList listener waits in case
 * the window narrows); ryze-sticky-atc.css hides the bar there as the
 * second lock. Once built, the bar publishes its height as --ryze-sticky-h
 * on :root and sets body.ryze-has-sticky-atc: that reserves the room at
 * the end of the footer (the SVNTY spacer) and lifts WhatsApp, the promo
 * pill and the cart toast by exactly the bar's height.
 *
 * Unchanged contracts: product.js variant data, the bidirectional sync
 * with Dawn's variant picker (800ms scroll hold), the add path
 * (POST /cart/add.js, the status check before any success), the badge
 * chain, the cartUpdate publish, the drawer and RyzeToast.
 */
(function () {
  'use strict';
  if (window.__ryzeStickyAtcInit) return;
  window.__ryzeStickyAtcInit = true;

  /* THE GATE: one breakpoint, read from the viewport, never the input device
     (an iPad is coarse too). Mirrors the CSS lock in ryze-sticky-atc.css. */
  var PHONE_QUERY = '(max-width: 989px)';
  var MENU_CLOSE_MS = 170;
  var QTY_MAX = 10;

  /* ── Icons ───────────────────────────────────────────────── */
  var CHECK_SVG = '<svg style="display:inline-block;vertical-align:middle;margin-right:4px;margin-top:-1px" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
  var MINUS_SVG = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false"><line x1="5" y1="12" x2="19" y2="12"/></svg>';
  var PLUS_SVG = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>';
  /* The caret's box is its glyph (SVNTY P17-17): 10 x 6, no dead width. */
  var CARET_SVG = '<svg class="ryze-sticky-atc__caret" width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><polyline points="1 1 5 5 9 1"/></svg>';

  /* ── State ────────────────────────────────────────────────── */
  var bar = null;
  var nameEl = null;
  var priceEl = null;
  var thumbEl = null;
  var pickWrap = null;
  var selectBtn = null;
  var selectLabel = null;
  var menu = null;
  var menuOpen = false;
  var menuCloseTimer = null;
  var qtyDisplay = null;
  var minusBtn = null;
  var plusBtn = null;
  var qty = 1;
  var atcBtn = null;
  var busy = false;
  var currentVariantId = null;
  var currentAvailable = true;
  var variants = [];
  var ignoreNextPDPSync = false; // prevent loops when we trigger PDP change

  var reduceMotion = false;
  try {
    reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {}

  function el(tag, className) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    return node;
  }

  /* ── Currency formatter (EGP) ────────────────────────────── */
  /* ONE MONEY VOICE (dev2.15-fix-pdp, 30 Oct 2026): the cards' Liquid
     `money_without_trailing_zeros`, "LE 850" / "LE 1,199"; a real fraction
     keeps its two digits ("LE 1,199.50"), exactly as the filter does. */
  function formatMoney(cents) {
    var whole = Math.round(cents) % 100 === 0;
    return 'LE ' + (cents / 100).toLocaleString('en-EG', {
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2,
    });
  }

  /* THE PRICE VOICE (locked, inventory #4/#5/#22): a strikethrough plus the
     sale figure when Shopify carries a compare-at above the price, otherwise
     the single figure. The red chip is derived at render time from the same
     two real variant fields - no rate constant exists anywhere. Text nodes
     only, so nothing here can inject markup. The figures are <s>/<strong>,
     never <span>: ryze-custom.css's body-span rule (0,2,2) un-bolds every
     span, which is why the capsule's sale figure never rendered at its
     authored 700. The chip keeps its locked span + classes verbatim. */
  /* SOLD OUT BEATS SALE (dev2.15-fix-pdp, the refuter's find): the buy box
     hides its -25% on a sold-out variant (Dawn: .price--sold-out
     .price__badge-sale none), so the bar's price line drops its chip there
     too; the struck figure and the price stay. */
  function renderPrice(target, cents, compareAtCents, available) {
    target.textContent = '';
    var now = el('strong', 'ryze-sticky-atc__price-now');
    now.textContent = formatMoney(cents);
    if (compareAtCents != null && compareAtCents > cents) {
      var was = el('s', 'ryze-sticky-atc__price-was');
      was.textContent = formatMoney(compareAtCents);
      target.appendChild(was);
      target.appendChild(now);
      var pct = Math.round(((compareAtCents - cents) / compareAtCents) * 100);
      if (pct > 0 && available !== false) {
        var chip = el('span', 'ryze-pill ryze-pill--discount');
        chip.textContent = '-' + pct + '%';
        target.appendChild(chip);
      }
    } else {
      target.appendChild(now);
    }
  }

  /* ONE SIZE VOICE (dev2.15-fix-pdp): "50 ML", the cards' spelling; the
     option value is "50ml", the CSS uppercases it. */
  function sizeLabel(v) {
    var raw = (v && (v.option1 || v.title)) || '';
    return String(raw).replace(/(\d)\s*ml\b/i, '$1 ml');
  }

  function isSingle() {
    return variants.length <= 1;
  }

  /* ── Build sticky bar DOM ────────────────────────────────── */
  function buildBar() {
    bar = el('div', 'ryze-sticky-atc');
    /* Rendered at the end of <body>, outside every landmark: the bar is its
       own complementary region, named by the one thing it does (SVNTY P17-17).
       Hidden states use visibility (CSS), so nothing in it is focusable or
       announced while it is off screen. */
    bar.setAttribute('role', 'complementary');
    bar.setAttribute('aria-label', 'Add to cart');

    var inner = el('div', 'ryze-sticky-atc__inner');

    /* THE PRICE LINE: name left, the price voice right. */
    var meta = el('div', 'ryze-sticky-atc__meta');
    nameEl = el('div', 'ryze-sticky-atc__name');
    priceEl = el('div', 'ryze-sticky-atc__price');
    meta.appendChild(nameEl);
    meta.appendChild(priceEl);

    /* THE ROW: thumbnail / SIZE / stepper / button, one 48px line. */
    var row = el('div', 'ryze-sticky-atc__row');

    thumbEl = el('img', 'ryze-sticky-atc__thumb');
    thumbEl.alt = '';
    thumbEl.loading = 'lazy';
    thumbEl.decoding = 'async';
    // Intrinsic size = the 200x200 CDN crop setThumb requests; CSS sets the
    // rendered box (W4 2026-10-18 audit: the one PDP image without dims).
    thumbEl.width = 200;
    thumbEl.height = 200;
    row.appendChild(thumbEl);

    /* SIZE: SVNTY's chip + upward listbox, one value per variant. */
    pickWrap = el('div', 'ryze-sticky-atc__pick');
    selectBtn = el('button', 'ryze-sticky-atc__select');
    selectBtn.type = 'button';
    selectBtn.setAttribute('aria-haspopup', 'listbox');
    selectBtn.setAttribute('aria-expanded', 'false');
    selectBtn.setAttribute('aria-controls', 'RyzeStickyAtcSizes');
    selectLabel = el('b', 'ryze-sticky-atc__select-label');
    selectLabel.textContent = 'Size';
    selectBtn.appendChild(selectLabel);
    selectBtn.insertAdjacentHTML('beforeend', CARET_SVG);
    menu = el('div', 'ryze-sticky-atc__menu');
    menu.id = 'RyzeStickyAtcSizes';
    menu.setAttribute('role', 'listbox');
    menu.setAttribute('aria-label', 'Size');
    menu.hidden = true;
    pickWrap.appendChild(selectBtn);
    pickWrap.appendChild(menu);
    row.appendChild(pickWrap);

    /* QUANTITY: the boxed stepper. */
    var qtyWrap = el('div', 'ryze-sticky-atc__qty');
    qtyWrap.setAttribute('role', 'group');
    qtyWrap.setAttribute('aria-label', 'Quantity');
    minusBtn = el('button', 'ryze-sticky-atc__qty-btn');
    minusBtn.type = 'button';
    minusBtn.setAttribute('aria-label', 'Decrease quantity');
    minusBtn.innerHTML = MINUS_SVG;
    qtyDisplay = el('output', 'ryze-sticky-atc__qty-val');
    qtyDisplay.setAttribute('aria-live', 'polite');
    qtyDisplay.textContent = '1';
    plusBtn = el('button', 'ryze-sticky-atc__qty-btn');
    plusBtn.type = 'button';
    plusBtn.setAttribute('aria-label', 'Increase quantity');
    plusBtn.innerHTML = PLUS_SVG;
    qtyWrap.appendChild(minusBtn);
    qtyWrap.appendChild(qtyDisplay);
    qtyWrap.appendChild(plusBtn);
    row.appendChild(qtyWrap);

    /* THE RYZE BUTTON (owner-locked standard, button-standard.md). */
    atcBtn = el('button', 'ryze-sticky-atc__btn');
    atcBtn.type = 'button';
    atcBtn.textContent = 'Add to cart';
    row.appendChild(atcBtn);

    inner.appendChild(meta);
    inner.appendChild(row);
    bar.appendChild(inner);
    document.body.appendChild(bar);
    document.body.classList.add('ryze-has-sticky-atc');

    minusBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      stepQty(-1);
    });
    plusBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      stepQty(1);
    });
    selectBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (menuOpen) closeMenu(false);
      else openMenu();
    });
    menu.addEventListener('keydown', onMenuKeydown);
    /* Tabbing (or VoiceOver moving) out of the open menu closes it. */
    menu.addEventListener('focusout', function (e) {
      if (menuOpen && e.relatedTarget && !pickWrap.contains(e.relatedTarget)) closeMenu(true);
    });
    /* One open menu; an outside press and Escape close it (SVNTY). */
    document.addEventListener('pointerdown', function (e) {
      if (menuOpen && !pickWrap.contains(e.target)) closeMenu(false);
    }, true);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && menuOpen) {
        closeMenu(false);
        focusQuietly(selectBtn);
      }
    });
    atcBtn.addEventListener('click', handleAddToCart);

    renderQty();

    /* The exact bar height, so the footer spacer and every lifted surface
       reserve exactly that much (SVNTY's --svnty-sticky-h). */
    publishHeight();
    if ('ResizeObserver' in window) {
      new ResizeObserver(publishHeight).observe(bar);
    }
    window.addEventListener('resize', publishHeight);
  }

  function publishHeight() {
    if (!bar) return;
    var h = bar.offsetHeight; // transform-independent; 0 while display:none (desktop)
    if (h > 0) document.documentElement.style.setProperty('--ryze-sticky-h', h + 'px');
    queueBand();
  }

  function focusQuietly(node) {
    if (!node) return;
    try {
      node.focus({ preventScroll: true });
    } catch (e) {
      node.focus();
    }
  }

  /* ── The stepper ─────────────────────────────────────────── */
  /* Bounds use aria-disabled, never real disabled: a control that may hold
     focus must not blur to body (button standard, hard rule 2). */
  function stepQty(delta) {
    var next = qty + delta;
    if (next < 1 || next > QTY_MAX) return;
    qty = next;
    renderQty();
  }

  function renderQty() {
    if (!qtyDisplay) return;
    qtyDisplay.textContent = String(qty);
    setAriaDisabled(minusBtn, qty <= 1);
    setAriaDisabled(plusBtn, qty >= QTY_MAX);
  }

  function setAriaDisabled(node, off) {
    if (!node) return;
    if (off) node.setAttribute('aria-disabled', 'true');
    else node.removeAttribute('aria-disabled');
  }

  /* ── THE SIZE MENU (SVNTY's upward listbox) ──────────────── */
  function renderMenu() {
    menu.textContent = '';
    variants.forEach(function (v) {
      var opt = el('button', 'ryze-sticky-atc__option');
      opt.type = 'button';
      opt.tabIndex = -1;
      opt.setAttribute('role', 'option');
      opt.setAttribute('data-variant-id', String(v.id));
      opt.setAttribute('aria-selected', Number(v.id) === Number(currentVariantId) ? 'true' : 'false');
      if (!v.available) opt.setAttribute('aria-disabled', 'true');
      var label = el('b', 'ryze-sticky-atc__option-label');
      label.textContent = sizeLabel(v);
      var price = el('div', 'ryze-sticky-atc__option-price');
      if (v.available) renderPrice(price, v.price, v.compare_at_price);
      else price.textContent = 'Sold out';
      opt.appendChild(label);
      opt.appendChild(price);
      opt.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (!v.available) return;
        chooseVariant(v);
      });
      menu.appendChild(opt);
    });
  }

  function openMenu() {
    if (!menu || isSingle() || !variants.length) return;
    if (menuCloseTimer) {
      clearTimeout(menuCloseTimer);
      menuCloseTimer = null;
    }
    renderMenu();
    menu.hidden = false;
    menu.classList.remove('is-closing');
    void menu.offsetWidth; // commit the start pose so the 200ms entry runs
    menu.classList.add('is-open');
    pickWrap.classList.add('is-open');
    selectBtn.setAttribute('aria-expanded', 'true');
    menuOpen = true;
    focusQuietly(
      menu.querySelector('[role="option"][aria-selected="true"]') ||
      menu.querySelector('[role="option"]:not([aria-disabled="true"])')
    );
  }

  function closeMenu(instant) {
    if (!menuOpen || !menu) return;
    menuOpen = false;
    pickWrap.classList.remove('is-open');
    selectBtn.setAttribute('aria-expanded', 'false');
    if (instant || reduceMotion) {
      menu.classList.remove('is-open');
      menu.classList.remove('is-closing');
      menu.hidden = true;
      return;
    }
    menu.classList.add('is-closing');
    menuCloseTimer = setTimeout(function () {
      menuCloseTimer = null;
      menu.classList.remove('is-open');
      menu.classList.remove('is-closing');
      menu.hidden = true;
    }, MENU_CLOSE_MS);
  }

  /* Roving focus inside the open menu; Enter/Space are the buttons' own. */
  function onMenuKeydown(e) {
    var options = Array.prototype.slice.call(
      menu.querySelectorAll('[role="option"]:not([aria-disabled="true"])')
    );
    if (e.key === 'Tab') {
      closeMenu(true);
      return;
    }
    if (!options.length) return;
    var i = options.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      options[(i + 1) % options.length].focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      options[(i - 1 + options.length) % options.length].focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      options[0].focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      options[options.length - 1].focus();
    }
  }

  function chooseVariant(v) {
    selectVariant(v);
    syncPDPVariant(v);
    closeMenu(false);
    focusQuietly(selectBtn);
  }

  function renderPick() {
    /* style.display, not [hidden]: an author display rule outguns [hidden]
       (THE HIDDEN-DISPLAY TRAP). One variant = no menu, like the pills before. */
    pickWrap.style.display = isSingle() ? 'none' : '';
  }

  /* ── Get product handle from URL ─────────────────────────── */
  function getProductHandle() {
    var path = window.location.pathname;
    if (!path.includes('/products/')) return null;
    var handle = path.split('/products/')[1];
    if (!handle) return null;
    return handle.split('?')[0].split('#')[0].replace(/\/$/, '');
  }

  /* ── Fetch all variant data via product.js ───────────────── */
  /* CODE-07 (fixes 2026-10-18): the bar is BUILT only after its data arrived.
     It used to be built first, so a failed product.js left the body class and
     --ryze-sticky-h reserving room for a bar that never showed. The status is
     read before the parse; any failure builds nothing (the PDP form stays the
     one way to buy) and a later phone-gate crossing may try again. */
  var fetching = false;
  function fetchAndInit() {
    var handle = getProductHandle();
    if (!handle || fetching) return;
    fetching = true;

    // product.js returns prices in CENTS (unlike product.json which uses major units)
    fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('product.js ' + res.status);
        return res.json();
      })
      .then(function (product) {
        if (!product || !product.variants || !product.variants.length) {
          throw new Error('product.js: no variants');
        }
        if (bar) return;
        buildBar();

        nameEl.textContent = product.title;

        if (product.featured_image) {
          setThumb(product.featured_image);
        }

        // Map variants - product.js prices are already in cents
        variants = product.variants.map(function (v) {
          return {
            id: v.id,
            title: v.title,
            option1: v.option1,
            option2: v.option2,
            option3: v.option3,
            price: v.price,          // already in cents
            compare_at_price: v.compare_at_price, // cents, or null when not on sale
            available: v.available,
            featured_image: v.featured_image
          };
        });

        renderPick();

        // Determine initially selected variant from PDP
        var initialId = getSelectedVariantIdFromPDP();
        var initial = variants.find(function (v) { return v.id === initialId; });
        if (!initial) initial = variants.find(function (v) { return v.available; }) || variants[0];
        selectVariant(initial);
        publishHeight();

        // Now set up scroll observer and variant change listener
        setupObserver();
        listenForVariantChanges();
      })
      .catch(function (err) {
        console.warn('[RYZE Sticky ATC] Could not load product data; no bar built:', err);
      })
      .then(function () {
        fetching = false;
      });
  }

  /* ── Get currently selected variant ID from PDP form ─────── */
  function getSelectedVariantIdFromPDP() {
    var input = document.querySelector('input.product-variant-id[name="id"]');
    if (input && input.value) return Number(input.value);

    // Fallback: parse from data-selected-variant
    var script = document.querySelector('variant-selects [data-selected-variant]');
    if (script) {
      try {
        var sv = JSON.parse(script.textContent);
        return sv.id;
      } catch (e) {}
    }
    return null;
  }

  /* ── Set thumbnail (with Shopify CDN resizing) ──────────── */
  function setThumb(imgSrc) {
    if (!imgSrc) return;
    var src = typeof imgSrc === 'string' ? imgSrc : (imgSrc.src || imgSrc);
    if (!src) return;
    // Resize via Shopify CDN
    if (src.includes('cdn.shopify.com')) {
      // Remove any existing size suffix and add our own
      src = src.replace(/(_\d+x\d+|_\d+x|_x\d+)(\.\w+)/, '$2');
      src = src.replace(/(\.\w+)(\?|$)/, '_200x200_crop_center$1$2');
    }
    thumbEl.src = src;
  }

  /* ── Select a variant (update bar UI) ────────────────────── */
  function selectVariant(v) {
    currentVariantId = v.id;
    currentAvailable = v.available !== false;

    // Price from the variant's own fields. No promo layer to re-trigger.
    renderPrice(priceEl, v.price, v.compare_at_price, v.available !== false);

    // The SIZE chip names the chosen size; its accessible name says what it is.
    selectLabel.textContent = sizeLabel(v);
    selectBtn.setAttribute('aria-label', 'Size: ' + sizeLabel(v));
    if (menuOpen) {
      menu.querySelectorAll('[role="option"]').forEach(function (opt) {
        opt.setAttribute('aria-selected', Number(opt.dataset.variantId) === Number(v.id) ? 'true' : 'false');
      });
    }

    // Update thumbnail if variant has its own image
    if (v.featured_image && v.featured_image.src) {
      setThumb(v.featured_image.src);
    }

    refreshAtc();
  }

  /* ── The button's resting state ──────────────────────────── */
  /* Sold out is aria-disabled + a class, never real disabled: the state is
     set by script, on a control that may hold focus (design law 3). While
     an add is in flight the add path owns the label; it calls back here. */
  function refreshAtc() {
    if (!atcBtn || busy) return;
    atcBtn.classList.remove('success', 'loading');
    if (currentAvailable) {
      atcBtn.removeAttribute('aria-disabled');
      atcBtn.classList.remove('is-soldout');
      atcBtn.textContent = 'Add to cart';
    } else {
      atcBtn.setAttribute('aria-disabled', 'true');
      atcBtn.classList.add('is-soldout');
      atcBtn.textContent = 'Sold out';
    }
  }

  /* ── Sync PDP variant selector when the bar's size is chosen ── */
  function syncPDPVariant(variant) {
    var selects = document.querySelector('variant-selects');
    if (!selects) return;

    ignoreNextPDPSync = true;

    // Lock scroll position - Dawn's section re-render causes a scroll jump
    var scrollX = window.pageXOffset;
    var scrollY = window.pageYOffset;
    function holdScroll() { window.scrollTo(scrollX, scrollY); }
    window.addEventListener('scroll', holdScroll);
    // Release the lock after Dawn finishes re-rendering (~600ms covers the fetch + DOM swap)
    setTimeout(function () { window.removeEventListener('scroll', holdScroll); }, 800);

    // Strategy: match variant option values against PDP radio buttons
    // RYZE products have Size as option1 (e.g., "50 ml", "100 ml")
    var optionValue = variant.option1;
    if (!optionValue) return;

    // Try radios first (pill / swatch picker)
    var radios = selects.querySelectorAll('input[type="radio"]');
    if (radios.length > 0) {
      var matched = false;
      radios.forEach(function (radio) {
        // Normalize: trim whitespace, lowercase compare
        var radioVal = radio.value.trim().toLowerCase().replace(/\s+/g, '');
        var targetVal = optionValue.trim().toLowerCase().replace(/\s+/g, '');
        if (radioVal === targetVal) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          matched = true;
        }
      });
      if (matched) return;
    }

    // Try dropdowns
    var dropdowns = selects.querySelectorAll('select');
    dropdowns.forEach(function (sel) {
      Array.from(sel.options).forEach(function (opt) {
        var optVal = opt.value.trim().toLowerCase().replace(/\s+/g, '');
        var targetVal = optionValue.trim().toLowerCase().replace(/\s+/g, '');
        if (optVal === targetVal) {
          sel.value = opt.value;
          sel.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }

  /* ── Add to cart ─────────────────────────────────────────── */
  /* The contract is the capsule's, unchanged: POST /cart/add.js, read the
     body, a `status` field is a refusal and nothing else runs; success then
     paints the badges, opens the drawer and raises the toast. W7 adds only
     a busy guard and the class cleanup on the refusal path (the capsule
     left `.loading` on, pointer-events:none, so after any refusal its
     button stayed dead until reload). */
  function handleAddToCart() {
    if (busy || !currentVariantId) return;
    if (atcBtn.getAttribute('aria-disabled') === 'true') return;

    busy = true;
    atcBtn.classList.add('loading');
    atcBtn.setAttribute('aria-busy', 'true');
    atcBtn.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        items: [{ id: Number(currentVariantId), quantity: qty }]
      }),
    })
      .then(function (res) {
        /* CODE-07: status first; a refusal's body carries the reason, a 5xx
           or an HTML error page may carry none. */
        return res.json().catch(function () { return null; }).then(function (data) {
          if (!res.ok || !data || (data.status && data.status !== 200)) {
            var refusal = new Error((data && (data.description || data.message)) || '');
            refusal.ryzeRefusal = true;
            throw refusal;
          }
          return data;
        });
      })
      .then(function (data) {

        // Success
        atcBtn.classList.remove('loading');
        atcBtn.classList.add('success');
        atcBtn.innerHTML = CHECK_SVG + ' Added';

        updateCartBadge();
        openCartDrawer();

        // Show liquid glass toast notification
        if (window.RyzeToast) {
          var productTitle = document.querySelector('.product__title, h1.product-title');
          var name = productTitle ? productTitle.textContent.trim() : 'Item';
          window.RyzeToast.show(name, 'added');
        }

        setTimeout(function () {
          busy = false;
          atcBtn.removeAttribute('aria-busy');
          qty = 1;
          renderQty();
          refreshAtc();
        }, 2500);
      })
      .catch(function (err) {
        /* CODE-07 (port of svnty-pdp-sticky.js 704-707, 747-748): say WHY.
           The store's reason (description, else message) when it gave one,
           the house sentence for a network or server failure. The button
           reads "Not added" for the same 2 s, then comes back. */
        var reason = (err && err.ryzeRefusal && err.message) || 'Could not add to cart. Please try again.';
        /* A partial add (two asked, one left) still changed the bag: repaint
           the count from the store, as SVNTY's cartError publish does. */
        if (err && err.ryzeRefusal) updateCartBadge();
        if (window.RyzeToast && typeof window.RyzeToast.show === 'function') {
          window.RyzeToast.show(reason, 'error');
        }
        atcBtn.classList.remove('loading');
        atcBtn.textContent = 'Not added';
        setTimeout(function () {
          busy = false;
          atcBtn.removeAttribute('aria-busy');
          refreshAtc();
        }, 2000);
      });
  }

  /* ── Update header cart count + publish PubSub ─────────── */
  function updateCartBadge() {
    fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Dawn badge selectors
        document.querySelectorAll('.cart-count-bubble span[aria-hidden]').forEach(function (el) {
          el.textContent = cart.item_count;
        });
        document.querySelectorAll('.cart-count-bubble').forEach(function (el) {
          if (cart.item_count > 0) el.style.display = '';
        });
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          if (cart.item_count > 0) el.style.display = '';
        });
        // RYZE header badge
        document.querySelectorAll('.ryze-nav__cart-count').forEach(function (b) {
          b.textContent = cart.item_count;
          b.style.display = cart.item_count > 0 ? 'flex' : 'none';
          /* Bump ONLY on a real count change, and NEVER during the first second
             after boot: navigations write every badge fresh (the marker starts
             empty on a new page), and those load-time writes must initialise
             silently. The boot anchor is shared: whichever script writes first
             plants it, so all writers count from the same moment. */
          window.__ryzeBadgeBoot = window.__ryzeBadgeBoot || Date.now();
          var ryzeBadgeMoved = b.dataset.ryzeLastCount !== String(b.textContent);
          b.dataset.ryzeLastCount = String(b.textContent);
          if (ryzeBadgeMoved && Date.now() - window.__ryzeBadgeBoot > 1000) {
            b.classList.remove('ryze-badge-bump');
            void b.offsetWidth;
            b.classList.add('ryze-badge-bump');
          }
        });

        // Publish PubSub cartUpdate for all subscribers
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'sticky-atc',
            cartData: cart
          });
        }
      })
      .catch(function () {});
  }

  /* ── Open cart drawer (Dawn theme) ───────────────────────── */
  function openCartDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      fetch('/cart?sections=cart-drawer')
        .then(function (r) { return r.json(); })
        .then(function (sections) {
          if (sections['cart-drawer']) {
            var tmp = document.createElement('div');
            tmp.innerHTML = sections['cart-drawer'];
            var newContent = tmp.querySelector('cart-drawer');
            if (newContent) drawer.innerHTML = newContent.innerHTML;
          }
          if (typeof drawer.open === 'function') drawer.open();
        })
        .catch(function () {
          if (typeof drawer.open === 'function') drawer.open();
        });
      return;
    }
    /* No drawer (cart_type page): nothing more to do. updateCartBadge()
       already published the real cart; the old empty cartUpdate here handed
       every subscriber a payload with no cartData (CODE-27, 2026-10-18). */
  }

  /* The WhatsApp sweep (findWhatsAppWidgets / setWhatsAppPosition) was
     retired 2026-10-18 (CODE-27): RYZE's bubble is gone and no app widget is
     installed; the CSS lift in ryze-sticky-atc.css stays as the one guard. */

  /* ── Observe ATC button visibility ───────────────────────── */
  /* The bar shows only when ALL are true: the PDP submit is off-screen, the
     hamburger menu is closed (owner catch 2026-08-17: the bar floated over
     the open menu), NO DOOR IS OPEN (owner catch 2026-10-18: the bar sat over
     the open search sheet; html.ryze-locked is the one signal every door
     raises through ryze-header.js THE LOCK: the drawer, the search, any sheet
     built later) and the viewport is under the phone gate. One
     applyBarState() owns the class. ryze-sticky-atc.css carries the same
     door rule as the second lock. */
  /* THE PRICE STAYS UNCOVERED (dev2.15-fix-pdp, 30 Oct 2026; the sweep's
     P2-2): at 390 x 844 the buy box's price row sits at the foot of the
     first screen (y 725-758) while its Add to cart is below the fold, so the
     bar rose at first paint over that row: its top at y 751 cut 7.2 px off
     the figures. The bar now also steps away while the price row is inside
     the band the bar occupies (the viewport's bottom `bar height` px, one
     IntersectionObserver whose root is that band, rebuilt when the window or
     the bar changes height). Where the price clears the band (402 x 874,
     the owner's 440 x 956) the bar stays up at rest exactly as before; it
     never covers the price it repeats (the refuter's P2-2: the first build
     hid it whenever the price was anywhere on screen). */
  var atcAway = false;
  var priceInBand = false;
  var navMenuOpen = false;
  var doorOpen = false;
  var onPhone = true;
  var priceRow = null;
  var bandObserver = null;
  var bandTimer = null;
  function applyBarState() {
    if (!bar) return;
    var show = atcAway && !priceInBand && !navMenuOpen && !doorOpen && onPhone;
    if (!show) closeMenu(true);
    bar.classList.toggle('visible', show);
  }
  function watchBand() {
    if (!bar || !priceRow || !('IntersectionObserver' in window)) return;
    var h = bar.offsetHeight;
    if (!(h > 0)) return;
    if (bandObserver) bandObserver.disconnect();
    var top = Math.max(0, Math.round(window.innerHeight - h));
    bandObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) { priceInBand = entry.isIntersecting; });
        applyBarState();
      },
      { threshold: 0, rootMargin: '-' + top + 'px 0px 0px 0px' }
    );
    bandObserver.observe(priceRow);
  }
  function queueBand() {
    if (!priceRow) return;
    if (bandTimer) clearTimeout(bandTimer);
    bandTimer = setTimeout(function () { bandTimer = null; watchBand(); }, 120);
  }
  function setupObserver() {
    var submitBtn = document.querySelector('.product-form__submit');
    if (!submitBtn) return;
    priceRow = document.querySelector('.product__info-container [id^="price-"]');

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          atcAway = !entry.isIntersecting;
        });
        applyBarState();
      },
      { threshold: 0, rootMargin: '0px' }
    );

    observer.observe(submitBtn);
    watchBand();

    var navMenu = document.querySelector('.ryze-mobile-menu');
    if (navMenu) {
      new MutationObserver(function () {
        navMenuOpen = navMenu.classList.contains('is-open');
        applyBarState();
      }).observe(navMenu, { attributes: true, attributeFilter: ['class'] });
    }

    /* THE DOOR: html.ryze-locked (ryze-header.js THE LOCK, ref-counted) is
       raised by every door in the theme; the bar steps off while one holds
       it and comes back with its close. Read once for a door already open
       at boot (a search opened before the script ran). */
    var root = document.documentElement;
    var readDoor = function () {
      var open = root.classList.contains('ryze-locked');
      if (open === doorOpen) return;
      doorOpen = open;
      applyBarState();
    };
    readDoor();
    new MutationObserver(readDoor).observe(root, { attributes: true, attributeFilter: ['class'] });
  }

  /* ── Listen to Dawn's variant change events ──────────────── */
  function listenForVariantChanges() {
    // Method 1: Dawn PUB_SUB (fires when user interacts with PDP variant picker)
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        if (ignoreNextPDPSync) {
          ignoreNextPDPSync = false;
          return;
        }
        if (!event || !event.data) return;
        var pdpVariant = event.data.variant;
        if (!pdpVariant) return;

        // Find our local variant object by ID
        var v = variants.find(function (vv) { return Number(vv.id) === Number(pdpVariant.id); });
        if (v) {
          // Update image from PDP event data (more reliable than our stored data)
          if (pdpVariant.featured_media && pdpVariant.featured_media.preview_image) {
            v.featured_image = { src: pdpVariant.featured_media.preview_image.src };
          }
          selectVariant(v);
        } else {
          // Unknown variant - update what we can
          selectVariantRaw(pdpVariant.id, pdpVariant.price, pdpVariant.available);
          if (pdpVariant.featured_media && pdpVariant.featured_media.preview_image) {
            setThumb(pdpVariant.featured_media.preview_image.src);
          }
        }
      });
    }

    // Method 2: MutationObserver on the hidden variant ID input (fallback)
    var variantInput = document.querySelector('input.product-variant-id[name="id"]');
    if (variantInput) {
      var mo = new MutationObserver(function () {
        var newId = Number(variantInput.value);
        if (newId && newId !== currentVariantId) {
          var v = variants.find(function (vv) { return Number(vv.id) === newId; });
          if (v) selectVariant(v);
        }
      });
      mo.observe(variantInput, { attributes: true, attributeFilter: ['value'] });

      // Also listen for value property changes (Dawn sets .value programmatically)
      var desc = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
      if (desc && desc.set) {
        var originalSet = desc.set;
        Object.defineProperty(variantInput, 'value', {
          set: function (val) {
            originalSet.call(this, val);
            var newId = Number(val);
            if (newId && newId !== currentVariantId) {
              var v = variants.find(function (vv) { return Number(vv.id) === newId; });
              if (v) selectVariant(v);
            }
          },
          get: desc.get ? desc.get.bind(variantInput) : function () {
            return desc.get ? desc.get.call(this) : '';
          }
        });
      }
    }
  }

  /* ── Fallback: select by raw ID/price/available (no local variant object) ── */
  function selectVariantRaw(id, price, available) {
    currentVariantId = id;
    currentAvailable = available !== false;
    /* No variant object here, so no compare-at is knowable: render the plain
       figure rather than guessing one. */
    renderPrice(priceEl, price, null);
    refreshAtc();
  }

  /* ── Init ─────────────────────────────────────────────────── */
  function start() {
    if (bar) return;
    fetchAndInit(); // builds the bar once the data is in (CODE-07)
  }

  function boot() {
    // Only run on product pages
    if (!document.querySelector('product-info')) return;
    if (!document.querySelector('.product-form__submit')) return;

    var phoneQuery = null;
    try {
      phoneQuery = window.matchMedia(PHONE_QUERY);
    } catch (e) {}
    if (!phoneQuery) {
      start(); // no matchMedia: build; the CSS lock still hides it at 990+
      return;
    }

    onPhone = phoneQuery.matches;
    var onChange = function (e) {
      onPhone = e.matches;
      if (onPhone && !bar) start();
      else applyBarState();
    };
    if (phoneQuery.addEventListener) phoneQuery.addEventListener('change', onChange);
    else if (phoneQuery.addListener) phoneQuery.addListener(onChange);

    // Desktop: nothing is built, nothing is fetched, nothing observes.
    if (onPhone) start();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
